import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { getLog } from '@aurora/shared-utils/log';
import React, { useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../../enums';
import {
  PaginationDirection,
  PaginationUpdateStrategy
} from '../../../../helpers/ui/PaginationHelper/PaginationHelper';
import Icons from '../../../../icons';
import type { EndUserRouteAndParams } from '../../../../routes/endUserRoutes';
import useEndUserRoutes from '../../../../routes/useEndUserRoutes';
import useTranslation from '../../../useTranslation';
import Button from '../../Button/Button';
import { ButtonVariant } from '../../Button/enums';
import { IconColor, IconSize } from '../../Icon/enums';
import Icon from '../../Icon/Icon';
import { PagerLoadMoreVariant, PagerPosition } from '../enums';
import type { PagerLoadMorePreviousNextLinkableProps } from '../types';
import localStyles from './PagerLoadMorePreviousNextLinkable.module.css';

const log = getLog(module);

/**
 *  The `loadMore` variant for `Pager` which can be used to load previous more or load next results
 *  Displays a link that loads either previous or next results.
 *
 * @author Sravan Reddy
 */
const PagerLoadMorePreviousNextLinkable: React.FC<
  React.PropsWithChildren<PagerLoadMorePreviousNextLinkableProps>
> = ({
  pageInfo,
  loadPage,
  className,
  useCaret = true,
  clearLoadingAfterUpdate = false,
  variant = PagerLoadMoreVariant.NONE,
  position = PagerPosition.CENTER,
  title,
  children = null,
  paginationDirection = PaginationDirection.NEXT,
  queryParams,
  stopPropagation
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    SharedComponent.PAGER_LOAD_MORE_PREVIOUS_NEXT_LINKABLE
  );
  const { hasNextPage, hasPreviousPage } = pageInfo;
  const { router, Link } = useEndUserRoutes();
  const { params, route } = router.getCurrentRouteAndParams();
  const [loading, setLoading] = useState<boolean>(false);
  const isNextLoadMore = paginationDirection === PaginationDirection.NEXT;
  const isPreviousLoadMore = paginationDirection === PaginationDirection.PREV;

  if (textLoading || (isPreviousLoadMore && !hasPreviousPage) || (isNextLoadMore && !hasNextPage)) {
    return null;
  }
  const text = title || formatMessage('loadMore');
  const hasWrapper = !children;

  const paginationUpdateStrategy = isPreviousLoadMore
    ? PaginationUpdateStrategy.PREPEND
    : PaginationUpdateStrategy.APPEND;

  const finalQueryParams = {
    ...(isNextLoadMore && { [EndUserQueryParams.AFTER]: pageInfo.endCursor }),
    ...(isPreviousLoadMore && { [EndUserQueryParams.BEFORE]: pageInfo.startCursor }),
    ...queryParams
  };

  const onclick = (event?: React.MouseEvent): void => {
    // Prevent Link navigation and default scrolling
    event?.preventDefault?.();
    if (stopPropagation) {
      event?.stopPropagation?.();
    }

    setLoading(true);
    Promise.resolve(loadPage?.(paginationUpdateStrategy, paginationDirection, true))
      .finally(() => {
        if (clearLoadingAfterUpdate) {
          setLoading(false);
        }
      })
      .catch(error => {
        log.error(error, 'Error loading page of results');
      });
  };

  /**
   * Returns wrapper needed around show more link.
   */
  function wrapper(wrapped: React.ReactElement): React.ReactElement {
    return (
      <div
        className={cx(className, `d-flex justify-content-${position}`, {
          [`lia-pager-wrap-${variant}`]: variant !== PagerLoadMoreVariant.NONE
        })}
      >
        {wrapped}
      </div>
    );
  }

  const button = (
    <Link<EndUserRouteAndParams<EndUserPages>>
      route={route}
      query={finalQueryParams}
      params={params}
      passHref
      scroll={false}
    >
      <Button
        className={cx(
          { className },
          `lia-pager-wrap-${paginationDirection}`,
          'd-flex lia-g-loader-btn lia-pager'
        )}
        title={text}
        variant={ButtonVariant.LINK}
        loading={loading}
        onClick={onclick}
      >
        {useCaret && (
          <Icon
            icon={isPreviousLoadMore ? Icons.ChevronUpIcon : Icons.ChevronDownIcon}
            color={IconColor.LOAD_TEXT}
            size={IconSize.PX_16}
            className={cx('lia-g-mr-5')}
          />
        )}
        {text}
      </Button>
    </Link>
  );

  return hasWrapper ? wrapper(button) : button;
};

export default PagerLoadMorePreviousNextLinkable;
